import {
  QueryClient,
  useMutation,
  UseQueryResult,
} from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { usePostHog } from "@posthog/react";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Progress } from "components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { IdentityState } from "contexts/identity-context";
import * as QueryKeys from "data";
import { stopBatchJob } from "data/mutations-common";
import {
  Copy,
  Download,
  Gem,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { LinearProgressWithLabel } from "./linear-progress-with-label";
import { BatchConfig } from "./models";
import {
  isFailureStatus,
  isPermittedLegacy,
  ProcessBatchRow,
  statusColour,
} from "./utils";

interface BuildColumnsArgs {
  batchesQuery: UseQueryResult<BatchConfig[], unknown>;
  identityId: string;
  identityState: IdentityState;
  queryClient: QueryClient;
  onDeleteRow: (row: ReturnType<typeof ProcessBatchRow>) => void;
}

const statusBadgeClass = (status: string) => {
  const color = statusColour(status);
  switch (color) {
    case "success":
      return "border-success text-success";
    case "error":
      return "border-destructive text-destructive";
    case "info":
      return "border-info text-info";
    case "warning":
      return "border-warning text-warning";
    case "secondary":
      return "border-secondary-foreground text-secondary-foreground";
    default:
      return "border-info text-info";
  }
};

export const buildColumns = ({
  batchesQuery,
  identityId,
  identityState,
  queryClient,
  onDeleteRow,
}: BuildColumnsArgs): ColumnDef<ReturnType<typeof ProcessBatchRow>>[] => {
  const history = useHistory();

  const { enqueueMutationFailed } = useCustomSnackbars();
  const posthog = usePostHog();
  const { isPermitted } = usePermissions();

  const stopJobMutation = useMutation({
    mutationKey: [QueryKeys.batchesStopJob, identityId],
    mutationFn: (batchId: string) => stopBatchJob(batchId),
    onSuccess: async () => {
      await batchesQuery.refetch();
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.toString());
    },
  });

  const updateCancellingJob = (batchId: string) => {
    const updatedBatchConfigs = batchesQuery.data?.map(
      (batchConfig: BatchConfig) => {
        if (batchConfig.batchInfo.batchId === batchId) {
          return { ...batchConfig, status: "Cancelling..." };
        }
        return batchConfig;
      },
    );
    queryClient.setQueryData(
      [QueryKeys.batchesKey, identityId],
      updatedBatchConfigs,
    );
  };

  return [
    {
      accessorKey: "displayName",
      header: "Batch Name",
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="truncate">{row.original.displayName}</p>
          </TooltipTrigger>
          <TooltipContent>{row.original.displayName}</TooltipContent>
        </Tooltip>
      ),
    },
    {
      accessorKey: "batchId",
      header: "Batch ID",
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="cursor-pointer gap-1 max-w-full"
              onClick={() =>
                navigator.clipboard.writeText(row.original.batchId)
              }
            >
              <span className="truncate">{row.original.batchId}</span>
              <Copy className="shrink-0" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{row.original.batchId}</TooltipContent>
        </Tooltip>
      ),
    },
    {
      accessorKey: "rowCount",
      header: "Row Count",
    },
    {
      accessorKey: "creditCost",
      header: "Credit Cost",
      cell: ({ row }) => (
        <>
          {isFailureStatus(row.original.status) && <>0</>}

          {!isFailureStatus(row.original.status) && (
            <>
              {row.original.creditCost}
              {(row.original.status === "Running" ||
                row.original.status === "Quoted") && (
                <span className="text-muted-foreground"> (estimated)</span>
              )}
            </>
          )}
        </>
      ),
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      size: 140,
      cell: ({ row }) => formatTimestamp(row.original.startDate),
    },
    {
      accessorKey: "isPremium",
      header: "Premium",
      size: 100,
      cell: ({ row }) => {
        if (row.original.isPremium) {
          return (
            <div className="flex items-center justify-self-center h-full">
              <Gem className="text-info" />
            </div>
          );
        }
        return null;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      cell: ({ row }) => {
        const badge = (
          <Badge
            variant="outline"
            className={`${statusBadgeClass(row.original.status)} gap-1`}
          >
            {row.original.status}
            {row.original.status === "Running" && (
              <button
                type="button"
                className="flex items-center hover:opacity-70"
                onClick={() => {
                  updateCancellingJob(row.original.batchId);
                  stopJobMutation.mutate(row.original.batchId);
                }}
                aria-label="Cancel job"
              >
                <X />
              </button>
            )}
          </Badge>
        );
        return badge;
      },
    },
    {
      accessorKey: "progress",
      header: "Progress",
      cell: ({ row }) => {
        if (row.original.status === "Queued") {
          return (
            <div className="flex items-center w-full h-full">
              <div className="w-full mr-2">
                <Progress
                  className="h-5 rounded-sm **:data-[slot=progress-indicator]:bg-info"
                  value={100}
                />
              </div>
              <div className="min-w-9" />
            </div>
          );
        }
        return (
          <LinearProgressWithLabel
            value={
              isFailureStatus(row.original.status) ? 0 : row.original.progress
            }
            status={row.original.status}
          />
        );
      },
    },
    {
      accessorKey: "actions",
      header: "",
      size: 60,
      enableSorting: false,
      cell: ({ row }) => {
        const isEditable =
          row.original.status === "Draft" || row.original.status === "Quoted";
        const canShowDownload =
          row.original.status === "Completed" ||
          row.original.status === "Queued" ||
          row.original.status === "Running";
        const isDownloadable = row.original.status === "Completed";

        const canEdit = isPermitted(
          row.original.isPremium
            ? [PermissionsKeys.premiumDataUpdate, PermissionsKeys.batchUpdate]
            : [PermissionsKeys.batchUpdate],
        );
        const canDownload = isPermitted([
          row.original.isPremium
            ? PermissionsKeys.premiumDataRead
            : PermissionsKeys.batchRead,
        ]);

        const isDeletable = row.original.status !== "Running";
        const hasLegacyDelete = isPermittedLegacy(
          row.original.subscriptionProductKey ?? "",
          row.original.status,
        );
        const canDelete =
          hasLegacyDelete ||
          (isPermitted([PermissionsKeys.batchDelete]) &&
            (!row.original.isPremium ||
              isPermitted([PermissionsKeys.premiumDataDelete])));

        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!isEditable || !canEdit}
                  onSelect={() => {
                    history.push(Routes.batchCreateBatch, {
                      batchId: row.original.batchId,
                      seedBatchConfig: row.original.batchConfig,
                    });
                  }}
                >
                  <Pencil />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!canShowDownload || !isDownloadable || !canDownload}
                  onSelect={() => {
                    const a = document.createElement("a");
                    a.href = row.original.downloadPath ?? "";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(
                      row.original.downloadPath ?? "",
                    );
                    posthog.capture("user_downloaded_batch_job", {
                      id: row.original.batchId,
                      name: row.original.displayName,
                      rowCount: row.original.rowCount,
                      creditCost: row.original.creditCost,
                    });
                  }}
                >
                  <Download />
                  Download
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Copy />
                  Copy
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  disabled={!isDeletable || !canDelete}
                  onSelect={() => onDeleteRow(row.original)}
                >
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];
};
